import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useQuery } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi'
import JSConfetti from 'js-confetti'
import { detect } from 'detect-browser'
import { Container } from './styles'
import { GlobalContext } from '../../../contexts/GlobalContext'
import masks from '../../../util/masks'
import scrollUtils from '../../../util/scroll'
import { api, authHeaders } from '../../../services/api'
import Spinner from '../../../components/Spinner'
import { useGeolocation } from '../../../hooks/useGeolocation'
import mercadoPago from '../../../assets/images/mercadopago.png'
import escudo from '../../../assets/images/escudo.png'
import { addToCart } from '../../../util/cart'

const jsConfetti = new JSConfetti()

const emailUsuarioTeste = 'test_user_1878552748@testuser.com'

const browser = detect()
const isSafari = ['ios-webview', 'ios', 'safari'].includes(browser.name)

export default function () {
	const { registerPageAccess } = useGeolocation()

	const { user } = useContext(GlobalContext)

	const cartItemString = localStorage.getItem('zelai:carrinho')

	const plano_contratacao = cartItemString ? JSON.parse(cartItemString) : null

	const history = useHistory()

	const [isLoading, setIsLoading] = useState(false)
	const [paymentLoadingErrorMessage, setPaymentLoadingErrorMessage] = useState(null)

	const { data: planosProntuario } = useQuery({
		queryKey: ['get-lista-planos-prontuario'],
		queryFn: async () => {
			try {
				const response = await api.get('plano', {
					params: {
						modulo_id: 1
					}
				})

				let result = Object.groupBy(response, ({ identificador }) => {
					if (identificador.startsWith('salvavidas')) {
						return 'salvavidas'
					}

					if (identificador.startsWith('individual')) {
						return 'individual'
					}

					if (identificador.startsWith('familia')) {
						return 'familia'
					}

					if (identificador.startsWith('empresa')) {
						return 'empresa'
					}

					if (identificador.startsWith('medico')) {
						return 'medico'
					}

					throw new Error('Tipo de plano não tratado.')
				})

				return result
			} catch (e) {
				console.log(e)
			}
		},
		refetchOnWindowFocus: false
	})

	async function loadBricks() {
		const mp = new window.MercadoPago(process.env.REACT_APP_MERCADO_PAGO_KEY, {
			locale: 'pt-BR'
		})

		const bricksBuilder = mp.bricks()

		const email = process.env.REACT_APP_ENVIRONMENT === 'development' ?
			emailUsuarioTeste :
			user.pessoa.contato.find(c => c.tipo_contato_id === 6)?.contato

		async function renderCardPaymentBrick(bricksBuilder) {
			const settings = {
				initialization: {
					amount: plano_contratacao.valor / 100,
					payer: {
						email
					}
				},
				customization: {
					visual: {
						style: {
							theme: 'default'
						},
						texts: {
							formTitle: 'Cartão de crédito',
							formSubmit: 'Contratar'
						}
					},
					paymentMethods: {
						maxInstallments: 1,
						types: {
							excluded: ['debit_card']
						}
					}
				},
				callbacks: {
					onReady: async () => {
						setIsLoading(false)

						await api.post('plano/contratacao/criar_carrinho', {
							plano_id: plano_contratacao.id
						}, authHeaders())
					},
					onSubmit: async cardFormData => {
						try {
							await api.post('plano/contratacao', {
								token: cardFormData.token,
								plano: plano_contratacao.id
							}, authHeaders())

							toast.success('Sua assinatura foi realizada com sucesso!')
							
							localStorage.removeItem('zelai:carrinho')
							
							history.push('/dashboard/prontuario')

							jsConfetti.addConfetti()
						} catch (e) {
							toast.error(e.msg)
						}
					},
					onError: e => {
						toast.error(e.message)
					}
				}
			}

			window.cardPaymentBrickController = await bricksBuilder.create('cardPayment', 'cardPaymentBrick_container', settings)
		}

		await renderCardPaymentBrick(bricksBuilder)
	}

	function timeout(ms) {
		return new Promise((_, reject) =>
			setTimeout(() => reject(new Error('Timeout!')), ms)
		)
	}

	function timeout(ms) {
		return new Promise((_, reject) =>
			setTimeout(() => reject(new Error('Timeout')), ms)
		)
	}

	useEffect(() => {
		if (plano_contratacao) {
			async function exec() {
				try {
					await Promise.race([
						loadBricks(),
						timeout(15000)
					])
				} catch (e) {
					let errorMessage = 'Ocorreu um erro na comunicação com o <span class="font-semibold">Mercado Pago</span>. Você pode tentar novamente em alguns minutos'

					if (isSafari) {
						errorMessage += ' ou tentar utilizar outro navegador (recomenda-se o Google Chrome)'
					}

					errorMessage += '.'

					setPaymentLoadingErrorMessage(errorMessage)
				}
			}

			exec()
		} else {
			history.push('/dashboard')
		}

		return () => {
			window.cardPaymentBrickController?.unmount()
		}
	}, [])

	useEffect(() => {
		registerPageAccess({
			plano: plano_contratacao.id,
			pessoaId: user?.pessoa.id
		})
	}, [])

	useEffect(() => {
		scrollUtils.toTop()
	}, [])

	if (isLoading || !planosProntuario) {
		return <Spinner />
	}

	let upgradeMessage = null

	const eAnual = plano_contratacao.periodo === 'anual'

	if (!eAnual) {
		const planoIdentificador = plano_contratacao.identificador.replace('_anual', '')

		const [planoAtual, planoUpgrade] = planosProntuario[planoIdentificador]
		const totalPlanoMensal = planoAtual.valor * 12
		const diferencaAnual = totalPlanoMensal - planoUpgrade?.valor
		const desconto = Math.round((diferencaAnual * 100) / totalPlanoMensal)

		upgradeMessage = (
			<div className="flex flex-col gap-1 text-slate-600 font-normal text-sm bg-emerald-500 text-white p-3 rounded-xl mt-4 w-96 shadow-md">
				<span
					className="text-white"
					dangerouslySetInnerHTML={{
						__html: `Que tal um desconto de <span class="font-bold">${desconto}%</span> na anuidade do ZelAI? Com o nosso plano anual, você economiza e fica despreocupado.`
					}}
				/>

				<span
					onClick={() => {
						addToCart(planoUpgrade)

						// localStorage.setItem('zelai:carrinho', JSON.stringify({
						// 	expiracao: addDays(new Date(), 3),
						// 	id: planoUpgrade.id,
						// 	descricao: planoUpgrade.descricao,
						// 	valor: planoUpgrade.valor,
						// 	periodo: planoUpgrade.periodo,
						// 	modulo: {
						// 		id: planoUpgrade.modulo.id,
						// 		descricao: planoUpgrade.modulo.descricao
						// 	},
						// 	identificador: planoUpgrade.identificador
						// }))

						history.push('/auth/checkout')
					}}
					className="self-end mt-3 text-white font-semibold hover:text-emerald-100 cursor-pointer flex items-center gap-1"
				>
					Clique para conhecer
					<FiArrowRight size={16} />
				</span>
			</div>
		)
	}

	return (
		<Container className="flex flex-col items-center text-slate-800 mt-4 mb-8">
			<div className="flex gap-3 text-slate-800 items-center">
				<img src={mercadoPago} alt="" className="h-7" />

				<p>Pagamento seguro com Mercado Pago</p>
			</div>

			{isSafari && paymentLoadingErrorMessage && (
				<div className="p-3 rounded-md bg-yellow-400 w-96 mt-6 flex flex-col gap-2 items-center animated flash fast">
					<FiAlertTriangle size={22} />

					<div
						dangerouslySetInnerHTML={{
							__html: paymentLoadingErrorMessage
						}}
					/>
				</div>
			)}

			<h1 className="text-slate-800 font-bold mt-8" style={{ fontSize: 22 }}>Resumo da compra</h1>

			{paymentLoadingErrorMessage && (
				<div className="p-3 rounded-md bg-yellow-400 w-96 mt-6 flex flex-col gap-2 items-center animated flash fast">
					<FiAlertTriangle size={22} />

					<div
						dangerouslySetInnerHTML={{
							__html: paymentLoadingErrorMessage
						}}
					/>
				</div>
			)}

			<div className="mt-6 mb-2 w-96">Você está assinando:</div>

			<div className="bg-white px-4 py-6 rounded-xl w-96 flex flex-col gap-2 shadow-md">
				<span className="font-bold" style={{ fontSize: 18 }}>{plano_contratacao.modulo.descricao}</span>
				<span className="font-bold">{`${plano_contratacao.descricao} ${plano_contratacao.periodo} - ${masks.maskApply.currency(plano_contratacao.valor / 100)} / ${plano_contratacao.periodo === 'mensal' ? 'mês' : 'ano'}`}</span>
			</div>

			{/* {upgradeMessage} */}

			<div id="cardPaymentBrick_container" className="mb-8 mt-4 mx-4 w-96 shadow-md rounded-xl"></div>

			<div className="grid grid-cols-[5rem_auto] gap-2 mx-12 w-96">
				<img src={escudo} alt="" className="h-14 justify-self-center" />

				<div className="text-sm">Todos os pagamentos passam por nosso <span className="font-semibold">Sistema Antifraude</span>, portanto a cobrança em seu cartão de crédito poderá levar até 2 horas para ser realizada.</div>
			</div>
		</Container>
	)
}
